import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Container, Alert, CircularProgress, AppBar, Toolbar } from '@mui/material';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import UserProfile from '../components/UserProfile';
import './Auth.css';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { sendPasswordReset } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    setError(null);
    try {
      await sendPasswordReset(email);
      setMessage('If an account with that email exists, a password reset link has been sent.');
    } catch (err: any) {
      setError(err.message || 'Failed to send password reset email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Background pattern */}
      <div className="bg-pattern"></div>
      
      <Box 
        className="main-container"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh'
        }}
      >
        <AppBar
          position="static"
          elevation={0}
          sx={{ 
            color: 'white',
            flexShrink: 0
          }}
        >
          <Toolbar>
            <Typography 
              variant="h5" 
              component="div" 
              sx={{
                fontFamily: 'Dancing Script, cursive',
                fontWeight: 700,
                fontSize: '30px',
                cursor: 'pointer',
                color: 'white',
                '&:hover': {
                  opacity: 0.8,
                },
                ml: 1,
              }}
              onClick={() => {
                window.location.href = '/';
              }}
            >
              <p style={{ margin: 10, fontFamily: 'Soria, serif', fontWeight: 400 }}>Auditio</p>
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            
            {/* User Profile Component */}
            <UserProfile />
          </Toolbar>
        </AppBar>

        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 0,
          overflow: 'auto'
        }}>
          <Container component="main" maxWidth="sm" className="auth-container">
            <Box className="auth-box">
              <Typography component="h1" variant="h5" className="auth-title">
                Forgot Password
              </Typography>
              {message && <Alert severity="success" sx={{ mt: 2, mb: 2, width: '100%' }}>{message}</Alert>}
              {error && <Alert severity="error" sx={{ mt: 2, mb: 2, width: '100%' }}>{error}</Alert>}
              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="auth-input"
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  className="auth-button"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
                </Button>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Link to="/login" className="auth-link">
                    Back to Login
                  </Link>
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default ForgotPasswordPage;